import React, { useEffect, useState } from 'react';
import { Button, Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api_url } from '../../constant/constant';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import './TrainingReports.css';

const TrainingReports = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [departments, setDepartments] = useState([]);
  const [campaigns, setCampaigns] = useState([]);

  const [campaignInfo, setCampaignInfo] = useState(null);
  const [quizDaily, setQuizDaily] = useState([]);
  const [quizPerLesson, setQuizPerLesson] = useState([]);
  const [videoWatchedDaily, setVideoWatchedDaily] = useState([]);
  const [videoWatchedPerLesson, setVideoWatchedPerLesson] = useState([]);
  const [quizPercentages, setQuizPercentages] = useState({ passed: 0, notTaken: 0 });

  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const isRtl = document.documentElement.getAttribute('dir') === 'rtl';

  const colors = ['#34d399', '#60a5fa', '#f87171', '#fbbf24', '#a78bfa', '#4ade80'];
  const COLORS = ['#38bdf8', '#34d399', '#f87171', '#fbbf24', '#a78bfa', '#fb7185', '#60a5fa'];
  const pieColors = ['#10b981', '#ef4444', '#d1d5db'];

  useEffect(() => {
    fetchDepartments();
    fetchCampaigns();
  }, []);

  const fetchDepartments = async () => {
    try {
      const token = localStorage.getItem('userToken');
      const response = await fetch(`${api_url}/admin/main/departments`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const result = await response.json();
      if (result && result.data) setDepartments(result.data);
    } catch (err) {
      console.error('Error fetching departments:', err);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const token = localStorage.getItem('userToken');
      const response = await fetch(`${api_url}/learning-campaigns`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const result = await response.json();
      if (result.learning_campaigns?.data) setCampaigns(result.learning_campaigns.data);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('userToken');
      const response = await fetch(
        `${api_url}/report/learning/campaign-summary?campaign_id=${selectedCampaign}&department_id=${selectedDepartment}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      if ([401, 403, 429].includes(response.status)) {
        localStorage.removeItem('userToken');
        navigate('/login');
        return;
      }

      const result = await response.json();
      if (result.status === 'success') {
        setCampaignInfo(result.campaign);
        setQuizDaily(result.quiz_stats.quiz_taken_daily || []);
        setQuizPerLesson(result.quiz_stats.quiz_per_lesson || []);
        setVideoWatchedDaily(result.video_stats.watched_daily || []);
        setVideoWatchedPerLesson(result.video_stats.watched_per_lesson || []);
        setQuizPercentages({
          passed: result.quiz_stats.passed_percentage || 0,
          notTaken: result.quiz_stats.not_taken_percentage || 0,
        });
        setError(null);
      } else {
        throw new Error(result.message || 'Failed to fetch campaign data.');
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError(err.message || 'An error occurred while fetching data.');
    } finally {
      setIsLoading(false);
    }
  };

  const pieData = [
    { name: t('Passed'), value: quizPercentages.passed },
    { name: t('NotTaken'), value: quizPercentages.notTaken },
  ];

  return (
    <div className='container py-4 mb-4'>
      {/* Header */}
      <div className='container p-3'>
        <div className='d-flex justify-content-between align-items-center mb-3'>
          <div className='pageTitle'>{t('TrainingCampaignDashboard')}</div>
          <Button variant='primary' onClick={() => navigate(-1)}>
            {t('Back')}
          </Button>
        </div>
      </div>

      {/* Dropdowns */}
      <div className='mb-4 p-3 card'>
        <div className='p-3' style={{ backgroundColor: '#f0f4f8', borderRadius: '16px' }}>
          <div className='row g-3 align-items-center'>
            <div className='col-md-4'>
              <label className='form-label'>{t('SelectCampaign')}</label>
              <select
                className='form-select'
                value={selectedCampaign}
                onChange={(e) => setSelectedCampaign(e.target.value)}
              >
                <option value=''>{t('SelectCampaign')}</option>
                {campaigns.map((campaign) => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.title_en}
                  </option>
                ))}
              </select>
            </div>

            <div className='col-md-4'>
              <label className='form-label'>{t('SelectDepartments')}</label>
              <select
                className='form-select'
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
              >
                <option value=''>{t('SelectDepartment')}</option>
                {departments.map((dept) => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Button
                style={{ minWidth: '100px' }}
                className='btn btn-success '
                onClick={fetchData}
                disabled={!selectedCampaign}
              >
                {t('ApplyFilters')}
              </Button>
            </div>
          </div>
        </div>
      </div>
      {isLoading && <div className='text-center py-5'>{t('Loading')}...</div>}
      {error && <div className='text-danger text-center py-5'>{error}</div>}

      {!isLoading && !error && (
        <>
          {/* Summary Cards */}
          <div className='row g-3 mb-4'>
            <div className='col-md-3'>
              <Card className='p-3 text-center'>
                <h4 className='fw-bold'>{campaignInfo?.start_date || '--'}</h4>
                <small className='text-muted'>{t('CampaignStartDate')}</small>
              </Card>
            </div>
            <div className='col-md-3'>
              <Card className='p-3 text-center'>
                <h4 className='fw-bold'>{campaignInfo?.end_date || '--'}</h4>
                <small className='text-muted'>{t('CampaignEndDate')}</small>
              </Card>
            </div>
            <div className='col-md-3'>
              <Card className='p-3 text-center'>
                <h4 className='fw-bold'>{campaignInfo?.total_users || 0}</h4>
                <small className='text-muted'>{t('NumberOfEnrolledUsers')}</small>
              </Card>
            </div>
            <div className='col-md-3'>
              <Card className='p-3 text-center'>
                <h4 className='fw-bold'>{campaignInfo?.total_lessons || 0}</h4>
                <small className='text-muted'>{t('NumberOfLessons')}</small>
              </Card>
            </div>
          </div>

          {/* Charts */}
          <div className='card shadow-sm p-4 mb-4' style={{ direction: isRtl ? 'rtl' : 'ltr' }}>
            <h5 className={`mb-4 ${isRtl ? 'text-end' : 'text-start'}`}>
              {t('TrainingReportsInfo')}
            </h5>
            <div className='row g-4'>
              <div className='col-md-6'>
                <Card className='p-3'>
                  <h6 className={`mb-4 ${isRtl ? 'text-end' : 'text-start'}`}>
                    {t('QuizTakenDaily')}
                  </h6>
                  <ResponsiveContainer width='100%' height={300}>
                    <BarChart data={quizDaily}>
                      <XAxis
                        dataKey='date'
                        tick={{
                          textAnchor: isRtl ? 'middle' : 'end',
                        }}
                      />
                      <YAxis
                        orientation={isRtl ? 'right' : 'left'}
                        tick={{
                          dx: isRtl ? -0 : -15,
                          textAnchor: isRtl ? 'end' : 'start',
                        }}
                      />
                      <Tooltip />
                      <Bar dataKey='count' fill='#4299e1' />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              </div>
              <div className='col-md-6'>
                <Card className='p-3'>
                  <h6 className={`mb-4 ${isRtl ? 'text-end' : 'text-start'}`}>
                    {t('QuizTakenPerLesson')}
                  </h6>
                  <ResponsiveContainer width='100%' height={300}>
                    <BarChart data={quizPerLesson}>
                      <XAxis
                        dataKey='title'
                        angle={-30}
                        textAnchor={isRtl ? 'start' : 'end'}
                        interval={0}
                        height={100}
                      />
                      <YAxis
                        orientation={isRtl ? 'right' : 'left'}
                        tick={{
                          dx: isRtl ? 25 : -20,
                          textAnchor: isRtl ? 'start' : 'end',
                        }}
                      />
                      <Tooltip />
                      <Bar dataKey='count'>
                        {quizPerLesson.map((_, index) => (
                          <Cell key={index} fill={colors[index % colors.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              </div>
            </div>
          </div>

          {/* Video Charts */}
          <div className='card shadow-sm p-4 mb-4' style={{ direction: isRtl ? 'rtl' : 'ltr' }}>
            <h5 className={`mb-4 ${isRtl ? 'text-end' : 'text-start'}`}>
              {t('VideoTrainingStats')}
            </h5>
            <div className='row g-4'>
              <div className='col-md-6'>
                <Card className='p-3'>
                  <h6 className={`mb-4 ${isRtl ? 'text-end' : 'text-start'}`}>
                    {t('VideoWatchedDaily')}
                  </h6>
                  <ResponsiveContainer width='100%' height={300}>
                    <BarChart data={videoWatchedDaily}>
                      <XAxis dataKey='date' angle={-45} textAnchor={isRtl ? 'start' : 'end'} />
                      <YAxis
                        orientation={isRtl ? 'right' : 'left'}
                        tick={{
                          dx: isRtl ? -0 : -10,
                          textAnchor: isRtl ? 'end' : 'start',
                        }}
                      />
                      <Tooltip />
                      <Bar dataKey='count'>
                        {videoWatchedDaily.map((_, index) => (
                          <Cell key={index} fill={colors[index % colors.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              </div>
              <div className='col-md-6'>
                <Card className='p-3'>
                  <h6 className={`mb-4 ${isRtl ? 'text-end' : 'text-start'}`}>
                    {t('VideoWatchedPerLesson')}
                  </h6>
                  <ResponsiveContainer width='100%' height={300}>
                    <BarChart data={videoWatchedPerLesson}>
                      <XAxis
                        dataKey='title'
                        angle={-28}
                        textAnchor={isRtl ? 'start' : 'end'}
                        interval={0}
                        height={100}
                      />
                      <YAxis
                        orientation={isRtl ? 'right' : 'left'}
                        tick={{
                          dx: isRtl ? 25 : -20,
                          textAnchor: isRtl ? 'start' : 'end',
                        }}
                      />
                      <Tooltip />
                      <Bar dataKey='count'>
                        {videoWatchedPerLesson.map((_, index) => (
                          <Cell key={index} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              </div>
            </div>
          </div>

          {/* Percentage Cards */}
          <div className='row mt-4'>
            <div className='col-md-6'>
              <Card className='p-3 text-center'>
                <h6 className='text-success'>{t('PassedPercentage')}</h6>
                <h3 className='fw-bold'>{quizPercentages.passed}%</h3>
              </Card>
            </div>
            <div className='col-md-6'>
              <Card className='p-3 text-center'>
                <h6 className='text-danger'>{t('NotTakenPercentage')}</h6>
                <h3 className='fw-bold'>{quizPercentages.notTaken}%</h3>
              </Card>
            </div>
          </div>

          {/* Pie Chart */}
          <Card className='shadow-sm p-4 mt-4' style={{ direction: isRtl ? 'rtl' : 'ltr' }}>
            <h5 className={`mb-4 ${isRtl ? 'text-end' : 'text-start'}`}>
              {t('QuizCompletionBreakdown')}
            </h5>
            <ResponsiveContainer width='100%' height={300}>
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey='value'
                  nameKey='name'
                  cx='50%'
                  cy='50%'
                  outerRadius={100}
                  label={({ cx, cy, midAngle, outerRadius, value, index }) => {
                    const r = outerRadius + 20,
                      a = (-midAngle * Math.PI) / 180;
                    const x = cx + r * Math.cos(a),
                      y = cy + r * Math.sin(a);
                    return (
                      <text
                        x={x}
                        y={y}  
                        fill={pieColors[index % pieColors.length]}
                        textAnchor={x > cx ? 'start' : 'end'}
                        dominantBaseline='central'
                        style={{ fontSize: 14, direction: 'ltr', whiteSpace: 'nowrap' }}
                      >
                        {value}
                      </text>
                    );
                  }}
                >
                  {pieData.map((_, i) => (
                    <Cell key={i} fill={pieColors[i % pieColors.length]} />
                  ))}
                </Pie>

                <Legend
                  verticalAlign='bottom'
                  content={({ payload }) => {
                    const isRTL = pieData.some((d) => /[\u0600-\u06FF]/.test(d?.name));
                    return (
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          gap: 16,
                          flexWrap: 'wrap',
                          direction: isRTL ? 'rtl' : 'ltr',
                        }}
                      >
                        {payload?.map((e, i) => (
                          <span
                            key={i}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 8,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {isRTL ? (
                              <>
                                {e.value}
                                <span
                                  style={{
                                    width: 12,
                                    height: 12,
                                    background: e.color,
                                    borderRadius: 2,
                                  }}
                                />
                              </>
                            ) : (
                              <>
                                <span
                                  style={{
                                    width: 12,
                                    height: 12,
                                    background: e.color,
                                    borderRadius: 2,
                                  }}
                                />
                                {e.value}
                              </>
                            )}
                          </span>
                        ))}
                      </div>
                    );
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </>
      )}
    </div>
  );
};

export default TrainingReports;
